import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getAllConfigs,
    getCodes,
    getCheckConfigType,
    getAllCodes,
    getConfigDetails,
    savePageLevelConfig,
    checkExistPending,
} from '../../api/AccountsAPI/pageLevelConfigAPI';

// ── Async Thunks ───────────────────────────────────────────────────────────────

/** Step 1: top-level "Configuration For" dropdown */
export const fetchConfigForList = createAsyncThunk(
    'pageLevelConfig/fetchConfigForList',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getAllConfigs();
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load configuration list');
        }
    }
);

/** Step 2: "Code Type" dropdown, scoped to the selected Configuration For */
export const fetchCodeTypeList = createAsyncThunk(
    'pageLevelConfig/fetchCodeTypeList',
    async (configFor, { rejectWithValue }) => {
        try {
            const res = await getCodes(configFor);
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load code type list');
        }
    }
);

/** Step 3: resolve the underlying master [Type], then load the "All Code" dropdown */
export const fetchAllCodeList = createAsyncThunk(
    'pageLevelConfig/fetchAllCodeList',
    async ({ code, codetext, codefor }, { rejectWithValue }) => {
        try {
            const typeRes = await getCheckConfigType({ code, codetext, codefor });
            const typeList = typeRes.data?.Data || [];
            const resolvedType = typeList[0]?.Code || codetext;

            const res = await getAllCodes({ code, codetext: resolvedType, codefor });
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load code values');
        }
    }
);

/** Step 3b: whether this Configuration For / Code Type pair is already configured (locked) */
export const fetchExistPendingStatus = createAsyncThunk(
    'pageLevelConfig/fetchExistPendingStatus',
    async ({ Code, AllCodeval }, { rejectWithValue }) => {
        try {
            const res = await checkExistPending({ Code, AllCodeval });
            return !!res.data?.Data;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to check configuration status');
        }
    }
);

/** Grid: existing saved configuration entries for the selected Configuration For */
export const fetchConfigGrid = createAsyncThunk(
    'pageLevelConfig/fetchConfigGrid',
    async (configFor, { rejectWithValue }) => {
        try {
            const res = await getConfigDetails(configFor);
            const list = res.data?.Data || [];
            return list.filter(row => row.Configurationfor !== 'No Data');
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load configuration grid');
        }
    }
);

/** Save a new configuration mapping */
export const submitPageLevelConfig = createAsyncThunk(
    'pageLevelConfig/submitPageLevelConfig',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await savePageLevelConfig(payload);
            const status = res.data?.Data;
            if (status && status.toLowerCase() !== 'successfull' && status.toLowerCase() !== 'success') {
                return rejectWithValue(status);
            }
            return { configFor: payload.configfor };
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to save the configuration');
        }
    }
);

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    configForList: [],
    codeTypeList: [],
    allCodeList: [],
    gridList: [],
    isLocked: false,
    saveResult: null,

    loading: {
        configFor: false,
        codeType: false,
        allCode: false,
        grid: false,
        lockCheck: false,
        save: false,
    },
    errors: {
        configFor: null,
        codeType: null,
        allCode: null,
        grid: null,
        lockCheck: null,
        save: null,
    },
};

const pageLevelConfigSlice = createSlice({
    name: 'pageLevelConfig',
    initialState,
    reducers: {
        clearCodeTypeAndBelow: (state) => {
            state.codeTypeList = [];
            state.allCodeList = [];
            state.isLocked = false;
        },
        clearAllCodeList: (state) => {
            state.allCodeList = [];
            state.isLocked = false;
        },
        clearPLCSaveResult: (state) => {
            state.saveResult = null;
            state.errors.save = null;
        },
        resetPageLevelConfig: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchConfigForList.pending, s => { s.loading.configFor = true; s.errors.configFor = null; })
            .addCase(fetchConfigForList.fulfilled, (s, a) => { s.loading.configFor = false; s.configForList = a.payload; })
            .addCase(fetchConfigForList.rejected, (s, a) => { s.loading.configFor = false; s.errors.configFor = a.payload; });

        builder
            .addCase(fetchCodeTypeList.pending, s => { s.loading.codeType = true; s.errors.codeType = null; })
            .addCase(fetchCodeTypeList.fulfilled, (s, a) => { s.loading.codeType = false; s.codeTypeList = a.payload; })
            .addCase(fetchCodeTypeList.rejected, (s, a) => { s.loading.codeType = false; s.errors.codeType = a.payload; });

        builder
            .addCase(fetchAllCodeList.pending, s => { s.loading.allCode = true; s.errors.allCode = null; })
            .addCase(fetchAllCodeList.fulfilled, (s, a) => { s.loading.allCode = false; s.allCodeList = a.payload; })
            .addCase(fetchAllCodeList.rejected, (s, a) => { s.loading.allCode = false; s.errors.allCode = a.payload; });

        builder
            .addCase(fetchExistPendingStatus.pending, s => { s.loading.lockCheck = true; s.errors.lockCheck = null; })
            .addCase(fetchExistPendingStatus.fulfilled, (s, a) => { s.loading.lockCheck = false; s.isLocked = a.payload; })
            .addCase(fetchExistPendingStatus.rejected, (s, a) => { s.loading.lockCheck = false; s.errors.lockCheck = a.payload; });

        builder
            .addCase(fetchConfigGrid.pending, s => { s.loading.grid = true; s.errors.grid = null; })
            .addCase(fetchConfigGrid.fulfilled, (s, a) => { s.loading.grid = false; s.gridList = a.payload; })
            .addCase(fetchConfigGrid.rejected, (s, a) => { s.loading.grid = false; s.errors.grid = a.payload; });

        builder
            .addCase(submitPageLevelConfig.pending, s => { s.loading.save = true; s.errors.save = null; s.saveResult = null; })
            .addCase(submitPageLevelConfig.fulfilled, (s, a) => { s.loading.save = false; s.saveResult = a.payload; })
            .addCase(submitPageLevelConfig.rejected, (s, a) => { s.loading.save = false; s.errors.save = a.payload; });
    },
});

export const {
    clearCodeTypeAndBelow,
    clearAllCodeList,
    clearPLCSaveResult,
    resetPageLevelConfig,
} = pageLevelConfigSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────────
export const selectConfigForList   = s => s.pageLevelConfig.configForList;
export const selectCodeTypeList    = s => s.pageLevelConfig.codeTypeList;
export const selectAllCodeList     = s => s.pageLevelConfig.allCodeList;
export const selectGridList        = s => s.pageLevelConfig.gridList;
export const selectIsLocked        = s => s.pageLevelConfig.isLocked;
export const selectPLCSaveResult   = s => s.pageLevelConfig.saveResult;

export const selectConfigForLoading = s => s.pageLevelConfig.loading.configFor;
export const selectCodeTypeLoading  = s => s.pageLevelConfig.loading.codeType;
export const selectAllCodeLoading   = s => s.pageLevelConfig.loading.allCode;
export const selectGridLoading      = s => s.pageLevelConfig.loading.grid;
export const selectLockCheckLoading = s => s.pageLevelConfig.loading.lockCheck;
export const selectPLCSaveLoading   = s => s.pageLevelConfig.loading.save;
export const selectPLCSaveError     = s => s.pageLevelConfig.errors.save;
export const selectGridError        = s => s.pageLevelConfig.errors.grid;

export default pageLevelConfigSlice.reducer;
